// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartElementProperties.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartElementProperties model
 */
!(function() {
  var m = require('mobx');
  var setValue = require('share/utils/Izenda.setValue');
  var trackable = require('share/tracking/Izenda.Trackable');
  var customURLConstants = require('logic/data/Izenda.PropertiesBuilderConstants').CUSTOM_URL;
  var propertiesBuilderConstants = require('logic/data/Izenda.PropertiesBuilderConstants');
  var reportDesingerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var Uiconstants = require('logic/data/Izenda.UIConstants');

  const {
    filter,
    isEmpty,
    some,
    isNil,
    get,
    getOr,
    cloneDeep,
    trim,
    replace,
    merge,
    set,
    assignIn,
    isUndefined
  } = require('lodash/fp');

  function ReportPartElementProperties(defaultSort) {
    var props = {
      fieldItemVisible: true,
      dataFormattings: {
        function: '',
        functionInfo: {
          dataType: null,
          expression: null,
          expressionSyntax: null,
          extendedProperties: {},
          id: '',
          isOperator: null,
          name: null,
          syntax: null,
          userDefined: false
        },
        format: {},
        font: {
          family: Uiconstants.fontFamily.proxima,
          size: Uiconstants.defaultFontSize,
          bold: false,
          italic: false,
          underline: false,
          color: '',
          backgroundColor: ''
        },
        width: {
          value: Uiconstants.SIZE.WIDTH.VALUE
        },
        alignment: null,
        verticalAlignment: null,
        wordWrap: null,
        sort: defaultSort,
        color: {
          textColor: {
            rangePercent: null,
            rangeValue: null,
            value: null,
            allValues: null
          },
          cellColor: {
            rangePercent: null,
            rangeValue: null,
            value: null,
            allValues: null
          }
        },
        alternativeText: {
          rangePercent: null,
          rangeValue: null,
          value: null
        },
        customURL: {
          url: '',
          option: customURLConstants.DEFAULT_VALUE
        },
        embeddedJavascript: {
          script: ''
        },
        subTotal: {
          label: '',
          function: '',
          expression: '',
          dataType: '',
          format: {},
          previewResult: '',
          levels: []
        },
        grandTotal: {
          label: '',
          function: '',
          expression: '',
          dataType: '',
          format: {},
          previewResult: ''
        }
      },
      headerFormating: {
        font: {
          family: null,
          size: null,
          bold: null,
          italic: null,
          underline: null,
          color: null,
          backgroundColor: null
        },
        alignment: null,
        verticalAlignment: null,
        wordWrap: null,
        columnGroup: '',
        verticalHeader: false,
        verticalHeaderHeight: {
          value: Uiconstants.SIZE.HEIGHT.VALUE
        },
      },
      drillDown: {
        subReport: {
          selectedReport: null,
          style: null,
          reportPartUsed: null,
          reportFilter: true,
          mappingFields: [],
          selectedIconValue: {
            icon: null,
            value: null
          },
          viewSettingByLink: null
        }
      },
      otherProps: {},
      subReport: function() {
        return this.drillDown.subReport;
      },
      customURL: function() {
        return this.dataFormattings.customURL;
      },
      embeddedJavascript: function() {
        return this.dataFormattings.embeddedJavascript;
      },
      hasRangePercent: function() {
        const { textColor, cellColor } = this.dataFormattings.color;
        return !(isEmpty(textColor.rangePercent) && isEmpty(cellColor.rangePercent));
      }
    };

    m.extendObservable(trackable(this), props);

    this.dataFormattings.functionInfo.formatDataType = null;

    this.cleanDirty();
  }

  /**
   * Return value of model based on its key
   * @param  {string} key
   * @return {}
   */
  ReportPartElementProperties.prototype.getValueByKey = function(key) {
    if(!isUndefined(this[key]))
      return this[key];
    if(!isUndefined(this.getDataValueByKey(key)))
      return this.getDataValueByKey(key);
    if(!isUndefined(this.getHeaderValueByKey(key)))
      return this.getHeaderValueByKey(key);
    if(!isUndefined(this.getDrilldownValueByKey(key)))
      return this.getDrilldownValueByKey(key);

    return this.getOtherProps(key);
  };

  /**
   * Return value of data formattings based on its key
   * @param  {string} key
   * @return {}
   */
  ReportPartElementProperties.prototype.getDataValueByKey = function(key) {
    return cloneDeep(m.toJS(this.dataFormattings[key]));
  };

  /**
   * Return value of header formattings based on its key
   * @param  {string} key
   * @return {}
   */
  ReportPartElementProperties.prototype.getHeaderValueByKey = function(key) {
    return this.headerFormating[key];
  };

  /**
   * Return value of drill down config based on its key
   * @param  {string} key
   * @return {}
   */
  ReportPartElementProperties.prototype.getDrilldownValueByKey = function(key) {
    return this.drillDown[key];
  };

  /**
   * Update value of data formating
   * @param  {object} newVal
   * @return {null}
   */
  ReportPartElementProperties.prototype.setDataFormattings = m.action(function(newVal) {
    newVal = newVal || {};

    var dataFormattings = this.dataFormattings;
    var font = newVal.font || {};
    var width = newVal.width || {};
    var grandTotal = newVal.grandTotal || {};
    var subTotal = newVal.subTotal || {};

    newVal.functionInfo && this.setFunctionInfo(newVal.functionInfo);
    setValue(dataFormattings, 'function', newVal.function);
    setValue(dataFormattings, 'format', newVal.format);
    setValue(dataFormattings.font, 'family', font.family);
    setValue(dataFormattings.font, 'size', font.size);
    setValue(dataFormattings.font, 'bold', font.bold);
    setValue(dataFormattings.font, 'italic', font.italic);
    setValue(dataFormattings.font, 'underline', font.underline);
    setValue(dataFormattings.width, 'value', width.value);
    setValue(dataFormattings, 'alignment', newVal.alignment);
    setValue(dataFormattings, 'verticalAlignment', newVal.verticalAlignment);
    setValue(dataFormattings, 'wordWrap', newVal.wordWrap);
    setValue(dataFormattings.customURL, 'url', newVal.customURL && newVal.customURL.url);
    setValue(dataFormattings.customURL, 'option', newVal.customURL && newVal.customURL.option);
    setValue(dataFormattings.customURL, 'viewSettingByLink', newVal.customURL && newVal.customURL.viewSettingByLink);
    setValue(dataFormattings.customURL, 'selectedIconValue', newVal.customURL && newVal.customURL.selectedIconValue);

    setValue(
      dataFormattings.embeddedJavascript,
      'script',
      newVal.embeddedJavascript && newVal.embeddedJavascript.script
    );
    setValue(
      dataFormattings.embeddedJavascript,
      'viewSettingByLink',
      newVal.embeddedJavascript && newVal.embeddedJavascript.viewSettingByLink
    );
    setValue(
      dataFormattings.embeddedJavascript,
      'selectedIconValue',
      newVal.embeddedJavascript && newVal.embeddedJavascript.selectedIconValue
    );

    this.updateGrandTotalData(grandTotal);
    this.updateSubTotalData(subTotal);
    this.updateSetingByRange(dataFormattings.alternativeText, newVal.alternativeText);
    this.updateSetingByRange(dataFormattings.color.cellColor, get('color.cellColor', newVal));
    this.updateSetingByRange(dataFormattings.color.textColor, get('color.textColor', newVal));
    this.setSortValue(newVal.sort);

    dataFormattings.format = dataFormattings.format || {};
    dataFormattings.format.createNewHiddenPercenOfGroupField = this.needPercentageData();

    this.markDirty();
  });

  ReportPartElementProperties.prototype.setSortValue = m.action(function(newValue) {
    var dataFormattings = this.dataFormattings;

    setValue(dataFormattings, 'sort', newValue);
  });

  /**
   * Update setting by range
   */
  ReportPartElementProperties.prototype.updateSetingByRange = m.action(function(target, source) {
    source && m.extendObservable(target, source);
  });

  /**
   * Update setting by range
   */
  ReportPartElementProperties.prototype.needPercentageData = m.action(function() {
    var dataFormattings = this.dataFormattings || {};

    var alternativeText = dataFormattings.alternativeText;
    var cellColor = get('color.cellColor', dataFormattings);
    var textColor = get('color.textColor', dataFormattings);

    return hasPercentConfig(alternativeText) || hasPercentConfig(cellColor) || hasPercentConfig(textColor);

    function hasPercentConfig(config) {
      if (!isNil(get('rangePercent', config))) {
        return true;
      }

      return some(item => !isNil(item) && !isNil(item.rangePercent), config);
    }
  });

  /**
   * Set value for "PercentOfGroupRequiredForConditionalFormatting" param
   */
  ReportPartElementProperties.prototype.setPercentOfGroupRequiredForConditionalFormatting = m.action(function(isPercentOfGroupRquired)
  {
    this.dataFormattings.format  = this.dataFormattings.format || {};
    this.dataFormattings.format.percentOfGroupRequiredForConditionalFormatting = isPercentOfGroupRquired;
    this.markDirty();
  });

  /**
   * Update value of header formating
   * @param  {object} newVal
   * @return {null}
   */
  ReportPartElementProperties.prototype.setHeaderFormattings = m.action(function(newVal) {
    newVal = newVal || {};
    var headerFormating = this.headerFormating;
    var font = newVal.font || {};
    var verticalHeaderHeight = newVal.verticalHeaderHeight || {};

    setValue(headerFormating.font, 'family', font.family);
    setValue(headerFormating.font, 'size', font.size);
    setValue(headerFormating.font, 'bold', font.bold);
    setValue(headerFormating.font, 'italic', font.italic);
    setValue(headerFormating.font, 'underline', font.underline);
    setValue(headerFormating, 'wordWrap', newVal.wordWrap);
    setValue(headerFormating, 'alignment', newVal.alignment);
    setValue(headerFormating, 'verticalAlignment', newVal.verticalAlignment);
    setValue(headerFormating, 'columnGroup', trim(newVal.columnGroup));
    setValue(headerFormating.font, 'color', font.color);
    setValue(headerFormating.font, 'backgroundColor', font.backgroundColor);
    setValue(headerFormating, 'verticalHeader', newVal.verticalHeader);
    setValue(headerFormating.verticalHeaderHeight, 'value', verticalHeaderHeight.value);

    this.markDirty();
  });

  /**
   * Set DrillDown Config
   * @param {type} newVal
   */
  ReportPartElementProperties.prototype.setDrillDownConfig = m.action(function(newVal) {
    newVal = newVal || {};
    var drillDown = this.drillDown || {};
    var subReport = newVal.subReport || {};

    setValue(drillDown.subReport, 'selectedReport', subReport.selectedReport);
    setValue(drillDown.subReport, 'style', subReport.style);
    setValue(drillDown.subReport, 'reportPartUsed', subReport.reportPartUsed);
    setValue(drillDown.subReport, 'reportFilter', subReport.reportFilter);
    setValue(drillDown.subReport, 'mappingFields', subReport.mappingFields);
    setValue(drillDown.subReport, 'selectedIconValue', subReport.selectedIconValue);
    setValue(drillDown.subReport, 'viewSettingByLink', subReport.viewSettingByLink);

    this.markDirty();
  });

  /**
   * Update value of model: dataFormattings, headerFormatings and drilldown config
   * @param  {object} newVal
   * @return {null}
   */
  ReportPartElementProperties.prototype.updateData = m.action(function(properties) {
    properties = properties || {};
    this.setDataFormattings(properties.dataFormattings);
    this.setHeaderFormattings(properties.headerFormating);
    this.setDrillDownConfig(properties.drillDown);
    this.setOtherProps(null, merge(this.otherProps, properties.otherProps));
    this.fieldItemVisible = properties.fieldItemVisible;

    this.markDirty();
  });

  /**
   * Update grand total data
   * @param {Object} grandData
   */
  ReportPartElementProperties.prototype.updateGrandTotalData = m.action(function(grandData) {
    grandData = grandData || {};

    var dataFormattings = this.dataFormattings;
    var grandTotal = dataFormattings.grandTotal;

    setValue(grandTotal, 'label', !isNil(grandData.name) ? grandData.name : grandData.label);
    setValue(grandTotal, 'function', !isNil(grandData.functionName) ? grandData.functionName : grandData.function);
    setValue(grandTotal, 'expression', grandData.expression);
    setValue(grandTotal, 'dataType', !isNil(grandData.izendaDataType) ? grandData.izendaDataType : grandData.dataType);
    setValue(grandTotal, 'previewResult', grandData.previewResult);
    setValue(grandTotal, 'format', grandData.format);
  });

  /**
   * Update sub total data
   * @param {Object} subTotalData
   */
  ReportPartElementProperties.prototype.updateSubTotalData = m.action(function(subTotalData) {
    subTotalData = subTotalData || {};

    var dataFormattings = this.dataFormattings;
    var subTotal = dataFormattings.subTotal;

    setValue(subTotal, 'label', !isNil(subTotalData.name) ? subTotalData.name : subTotalData.label);
    setValue(
      subTotal,
      'function',
      !isNil(subTotalData.functionName) ? subTotalData.functionName : subTotalData.function
    );
    setValue(subTotal, 'expression', subTotalData.expression);
    setValue(
      subTotal,
      'dataType',
      !isNil(subTotalData.izendaDataType) ? subTotalData.izendaDataType : subTotalData.dataType
    );
    setValue(subTotal, 'previewResult', subTotalData.previewResult);
    setValue(subTotal, 'format', subTotalData.format);
  });

  /**
   * Get dirty status.
   */
  ReportPartElementProperties.prototype.checkDirty = function() {
    return this.isDirty;
  };

  /**
   * Clean dirty status.
   */
  ReportPartElementProperties.prototype.cleanDirty = function() {
    this.resetDirty();
  };

  ReportPartElementProperties.prototype.getDefaultsHeaderProperty = function(path, parent) {
    // In case of differing default props, if headerFormatting path differs from the parent, return the headerFormatting value.
    // If the headerFormatting is undefined or null, then return the parent path value.
    var value = get(path, this.headerFormating);
    return !isNil(value) ? value : get(path, parent);
  };

  ReportPartElementProperties.prototype.getUIStyle = function() {
    return {
      fontFamily: get('font.family', this.dataFormattings),
      fontSize: get('font.size', this.dataFormattings),
      bold: get('font.bold', this.dataFormattings),
      italic: get('font.italic', this.dataFormattings),
      underline: get('font.underline', this.dataFormattings),
      alignment: get('alignment', this.dataFormattings),
      verticalAlignment: get('verticalAlignment', this.dataFormattings),
      wordWrap: get('wordWrap', this.dataFormattings)
    };
  };

  ReportPartElementProperties.prototype.getHeaderCellStyle = function(parent) {
    return {
      fontFamily: this.getDefaultsHeaderProperty('font.family', parent),
      fontSize: this.getDefaultsHeaderProperty('font.size', parent),
      bold: this.getDefaultsHeaderProperty('font.bold', parent),
      italic: this.getDefaultsHeaderProperty('font.italic', parent),
      underline: this.getDefaultsHeaderProperty('font.underline', parent),
      textColor: this.getDefaultsHeaderProperty('font.color', parent),
      backgroundColor: this.getDefaultsHeaderProperty('font.backgroundColor', parent),
      alignment: this.getDefaultsHeaderProperty('alignment', parent),
      verticalAlignment: this.getDefaultsHeaderProperty('verticalAlignment', parent),
      wordWrap: this.getDefaultsHeaderProperty('wordWrap', parent)
    };
  };

  /**
   * Get other properties, these props can be different based on different report-type
   * @return {string} key
   */
  ReportPartElementProperties.prototype.getOtherProps = function(key) {
    return key ? this.otherProps[key] : this.otherProps;
  };

  /**
   * Set other properties, these props can be different based on different report-type
   * @return {}
   */
  ReportPartElementProperties.prototype.setOtherProps = m.action(function(key, newVal) {
    if (key) {
      // create new otherProps object so it will trigger re-render/computed field
      this.otherProps = set(key, newVal, this.otherProps);
    } else {
      this.otherProps = assignIn(this.otherProps, newVal);
    }
    this.markDirty();
  });

  /**
   * Set format
   * @method setFormat
   * @param  {json}  formatData Format data
   */
  ReportPartElementProperties.prototype.setFormat = m.action(function(formatData) {
    setValue(this.dataFormattings, 'format', formatData);
  });

  /**
   * Set format
   * @method setFormat
   * @param  {json}  formatData Format data
   */
  ReportPartElementProperties.prototype.setDataFormatByKey = m.action(function(key, value) {
    setValue(this.dataFormattings, key, value);
    this.markDirty();
  });

  /**
   * set function
   * @method setFunction
   * @param  {json}    functionData Function Data
   */
  ReportPartElementProperties.prototype.setFunction = m.action(function(functionData) {
    setValue(this.dataFormattings, 'function', functionData.id || '');
    this.setFunctionInfo(functionData);
  });

  /**
   * Update function info value.
   * @param {Object} functionData
   */
  ReportPartElementProperties.prototype.setFunctionInfo = m.action(function setFunctionInfo(functionData: Object) {
    this.dataFormattings.functionInfo.dataType = get('dataType', functionData);
    this.dataFormattings.functionInfo.expression = get('expression', functionData);
    this.dataFormattings.functionInfo.expressionSyntax = get('expressionSyntax', functionData);
    this.dataFormattings.functionInfo.extendedProperties = get('extendedProperties', functionData);
    this.dataFormattings.functionInfo.formatDataType = get('formatDataType', functionData);
    this.dataFormattings.functionInfo.id = getOr(null, 'id', functionData);
    this.dataFormattings.functionInfo.isOperator = get('isOperator', functionData);
    this.dataFormattings.functionInfo.name = getOr('', 'name', functionData);
    this.dataFormattings.functionInfo.syntax = get('syntax', functionData);
    this.dataFormattings.functionInfo.userDefined = get('userDefined', functionData);
  });

  /**
   * Check uniq link option in : Custom URL , Embbeded Javascript, Sub Report
   * @param {bool} popupContent
   * @param {string} currentPopupName
   * @returns {bool} this link option is uniq or not
   */
  ReportPartElementProperties.prototype.checkUniqLinkOption = function(popupContent, currentPopupName) {
    var excludeCurrentPopup = filter(function(option) {
      return option != currentPopupName;
    }, propertiesBuilderConstants.CHECK_UNIQ_OPTIONS);

    var isValid = true;

    excludeCurrentPopup.forEach(
      function(option) {
        var selectedIconValue = this[option].selectedIconValue || {};

        // If option contains style = Embedded, skip this option
        var isEmbeddedSubReport =
          this[option].style && this[option].style === reportDesingerConstants.DRIL_DOWN_STYLE.EMBEDDED;

        var isEmptySubReport =
          option === propertiesBuilderConstants.SUB_REPORT.NAME ? !get('selectedReport', this[option]) : false;

        if (
          !isEmbeddedSubReport &&
          !isEmptySubReport &&
          !this[option].viewSettingByLink && // only check for icon option
          popupContent.viewSettingByLink &&
          selectedIconValue.value === popupContent.selectedIconValue.value
        ) {
          isValid = false;
        }
      }.bind(this)
    );

    return isValid;
  };

  /**
   * Check if Sub report style = embedded & custom URL/embedded javascript view option = Link
   */
  ReportPartElementProperties.prototype.checkEmbeddedLinkOption = function(style) {
    style = style || this.subReport.style;

    if (
      style === reportDesingerConstants.DRILL_DOWN_STYLE_OPTION_VALUES.embedded &&
      (this.customURL.viewSettingByLink || this.embeddedJavascript.viewSettingByLink)
    ) {
      return false;
    }

    return true;
  };

  /**
   * After change function/format :
   * - Reset Interval and Starting and Ending Point
   */
  ReportPartElementProperties.prototype.changeFunctionFormatAction = m.action(function(style) {
    var otherProps = this.otherProps;
    var axis = otherProps.axis;

    if (axis) {
      axis.izTickInterval = null;
      axis.izTickCategoryInterval = null;
      axis.izStartingPoint = null;
      axis.izEndingPoint = null;
      axis.izCategory = false;
    }
  });

  /**
   * Update Grand Total/ Sub Total when change Field Name Alias
   * Update Expression in Grand/Sub Total
   */
  ReportPartElementProperties.prototype.updateGrandSubTotal = m.action(function(oldAlias, newAlias) {
    var dataFormattings = this.dataFormattings;
    var grandTotal = dataFormattings.grandTotal;
    var subTotal = dataFormattings.subTotal;
    var oldFieldName = `[${oldAlias}]`;
    var newFieldName = `[${newAlias}]`;

    // Update Grand Total
    !isEmpty(grandTotal.expression) &&
      (grandTotal.expression = replace(oldFieldName, newFieldName, grandTotal.expression));

    // Update Sub Total
    !isEmpty(subTotal.expression) && (subTotal.expression = replace(oldFieldName, newFieldName, subTotal.expression));
  });

  module.exports = ReportPartElementProperties;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartElementProperties.js